import { useEffect, useState } from "react";
import * as authApi from "../../api/auth";
import type { InvitationInfo } from "../../api/types";
import { STRENGTH_COLORS, passwordStrength } from "../../auth-validation";
import { setLoginPrefill } from "../../state/authPrefill";
import { PasswordInput } from "../../ui/PasswordInput";
import { useAuthConfig } from "../../state/useAuthConfig";
import { AuthCard } from "./AuthCard";
import { TERMS_NOT_AGREED, TermsAgreement } from "./TermsAgreement";

const ROLE_TEXT: Record<string, string> = {
  admin: "an administrator", annotator: "an annotator", viewer: "a read-only viewer",
};

/** Opened from the invitation email: shows who is invited, then lets them choose a username and password. */
export function AcceptInviteScreen({ token }: { token: string }) {
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState(token ? "" : "This invitation link is incomplete. Ask for a new one.");
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const strength = pw ? passwordStrength(pw) : null;
  const config = useAuthConfig();
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!token) return;
    let live = true;
    authApi.lookupInvitation(token)
      .then((i) => { if (live) setInfo(i); })
      .catch((e: Error) => { if (live) setLoadError(e.message); });
    return () => { live = false; };
  }, [token]);

  const submit = async () => {
    if (username.trim().length < 3) { setError("Choose a username of at least 3 characters."); return; }
    if (strength === "Too short (min 8 chars)" || strength === "Weak") { setError(`Password is ${strength.toLowerCase()}. Choose a stronger password.`); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    if (config.terms_version && !agreed) { setError(TERMS_NOT_AGREED); return; }
    setBusy(true);
    setError("");
    try {
      const user = await authApi.acceptInvitation(token, username.trim(), pw, confirm, config.terms_version ?? undefined);
      setLoginPrefill(user.username);
      window.location.hash = "#/login";
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <AuthCard>
      <h1>Accept invitation</h1>
      {loadError && <div className="error-text" role="alert" data-testid="invite-error">{loadError}</div>}
      {!loadError && !info && <div className="sub">Checking your invitation…</div>}
      {info && (
        <>
          <div className="sub" data-testid="invite-info">
            You have been invited to <b>{info.org_name}</b> as {ROLE_TEXT[info.role] ?? info.role}. Your account will use <b>{info.email}</b>.
          </div>
          <label className="caption" htmlFor="ai-user">Username</label>
          <input id="ai-user" className="input" placeholder="Choose a username" autoComplete="username" value={username}
            onChange={(e) => setUsername(e.target.value)} autoFocus />
          <label className="caption" htmlFor="ai-pw">Password</label>
          <PasswordInput id="ai-pw" value={pw} onChange={setPw} placeholder="Min 8 characters" autoComplete="new-password" />
          <div className="strength" data-testid="strength" style={{ color: strength ? STRENGTH_COLORS[strength] : undefined, minHeight: 14 }}>{strength ?? ""}</div>
          <label className="caption" htmlFor="ai-pw2">Confirm password</label>
          <PasswordInput id="ai-pw2" value={confirm} onChange={setConfirm} placeholder="Repeat password" onEnter={submit} autoComplete="new-password" />
          <TermsAgreement config={config} agreed={agreed} onChange={setAgreed} />
          <div className="error-text" role="alert" data-testid="accept-error">{error}</div>
          <button className="btn primary" disabled={busy} onClick={submit} data-testid="accept-submit">Create account</button>
        </>
      )}
      <div className="switch"><a className="btn flat" href="#/login" data-testid="to-login">Back to sign in</a></div>
    </AuthCard>
  );
}
